import { ReactNode } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  AutoAwesome, Cancel, CheckCircle, ErrorOutline, RemoveCircleOutline, WarningAmber,
} from '@mui/icons-material';
import { AutoCheck, AutoCheckItem } from '../../services/business/payoutService';
import { dateText } from './payoutShared';

type IconColor = 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info';

export function ActionIcon({
  title, color = 'default', onClick, disabled, children,
}: { title: string; color?: IconColor; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" color={color} onClick={onClick} disabled={disabled} aria-label={title}>{children}</IconButton>
      </span>
    </Tooltip>
  );
}

const REC: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  approve: { label: 'Looks genuine', color: 'success' },
  review: { label: 'Needs review', color: 'warning' },
  reject: { label: 'High risk', color: 'error' },
};

export function AutoCheckChip({ check, busy }: { check?: AutoCheck; busy?: boolean }) {
  if (busy || check?.state === 'running') {
    return <Chip size="small" icon={<CircularProgress size={12} />} label="Checking…" variant="outlined" />;
  }
  if (!check) return <Chip size="small" label="Not run" variant="outlined" />;
  if (check.state === 'error') return <Tooltip title={check.error || ''}><Chip size="small" icon={<ErrorOutline />} label="Check failed" variant="outlined" /></Tooltip>;
  const rec = REC[check.recommendation || 'review'];
  return (
    <Tooltip title={check.summary || ''}>
      <Chip size="small" color={rec.color} icon={<AutoAwesome />} label={`${check.score ?? 0} · ${rec.label}`} />
    </Tooltip>
  );
}

/** Extra friction when the admin is about to approve something the assistant has not cleared. */
export function ApprovalWarning({ check, subject }: { check?: AutoCheck; subject: string }) {
  if (!check || check.state !== 'done') {
    return <Alert severity="info">The verification assistant has not checked this {subject} yet. Consider running it first.</Alert>;
  }
  if (check.recommendation === 'approve') return <Alert severity="success">Assistant ({check.score}/100): {check.summary}</Alert>;
  const problems = (check.checks || []).filter((c) => c.status === 'fail' || c.status === 'warn');
  return (
    <Alert severity={check.recommendation === 'reject' ? 'error' : 'warning'}>
      <b>The assistant flagged this {subject} ({check.score}/100). Are you sure?</b>
      {problems.map((p) => <div key={p.key}>• {p.label}: {p.detail}</div>)}
    </Alert>
  );
}

export function suggestedReason(check?: AutoCheck): string {
  const failed = (check?.checks || []).filter((c) => c.status === 'fail');
  return failed.map((c) => `${c.label}: ${c.detail}`).join('\n').slice(0, 300);
}

const STATUS_ICON: Record<AutoCheckItem['status'], ReactNode> = {
  pass: <CheckCircle color="success" fontSize="small" />,
  warn: <WarningAmber color="warning" fontSize="small" />,
  fail: <Cancel color="error" fontSize="small" />,
  skip: <RemoveCircleOutline color="disabled" fontSize="small" />,
};

const EXTRACTED_LABELS: Record<string, string> = {
  source: 'Read from', name: 'Name', dateOfBirth: 'Date of birth', documentNumber: 'Document number',
  expiryDate: 'Expires', issuer: 'Issued by', bankName: 'Registered bank',
};

export function AutoCheckPanel({ check, onRun, busy }: { check?: AutoCheck; onRun: () => void; busy?: boolean }) {
  const running = busy || check?.state === 'running';
  const rec = check?.state === 'done' ? REC[check.recommendation || 'review'] : undefined;
  const extracted = Object.entries(check?.extracted || {}).filter(([, v]) => v);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
        <AutoAwesome color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={700} flex={1}>Verification assistant</Typography>
        {check?.ranAt && !running && <Typography variant="caption" color="text.secondary">{dateText(check.ranAt)}</Typography>}
        <AutoCheckChip check={check} busy={busy} />
        <Button size="small" startIcon={running ? <CircularProgress size={14} /> : <AutoAwesome />} onClick={onRun} disabled={running}>
          {check ? 'Re-run' : 'Run check'}
        </Button>
      </Stack>

      {!check && !running && (
        <Typography variant="body2" color="text.secondary">
          Not checked yet. Run it to read the documents and cross-check the details automatically.
        </Typography>
      )}
      {check?.state === 'error' && <Alert severity="error">{check.error}</Alert>}
      {check?.state === 'done' && (
        <>
          <Alert severity={rec?.color === 'success' ? 'success' : rec?.color === 'error' ? 'error' : 'warning'} sx={{ mb: 1.5 }}>
            <b>{check.score}/100 · {rec?.label}.</b> {check.summary}
          </Alert>
          <Stack gap={0.75}>
            {(check.checks || []).map((c) => (
              <Stack key={c.key} direction="row" gap={1} alignItems="flex-start">
                <Box pt={0.25}>{STATUS_ICON[c.status]}</Box>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={600}>
                    {c.label}{c.critical && <Typography component="span" variant="caption" color="text.secondary"> · key check</Typography>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{c.detail}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
          {extracted.length > 0 && (
            <Box mt={1.5} p={1.5} bgcolor="action.hover" borderRadius={1}>
              <Typography variant="overline">Read from the document</Typography>
              {extracted.map(([k, v]) => (
                <Typography key={k} variant="body2">{EXTRACTED_LABELS[k] || k}: <b>{v}</b></Typography>
              ))}
            </Box>
          )}
        </>
      )}
      <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
        Runs on your own server (OCR, barcode and check-digit validation, Stripe bank lookup). No data is sent to any AI service.
        It is advisory — you make the final decision.
      </Typography>
    </Paper>
  );
}
